import { HttpClient, HttpHeaders } from "@angular/common/http";
import {
  AngularFirestore,
} from "@angular/fire/firestore";
import * as moment from "moment-timezone";
import { ToastService } from "ng-uikit-pro-standard";
import { environment } from "src/environments/environment";
import { ProductBuilder } from "../product/product.builder";
import { Pass, ProductQuery } from "../product/product.factory";
import { Product } from "../product/product.model";
import { AuthService } from "../services";
import { Venue } from "../venue/venue.model";
import { CalendarEventBuilder } from "./event.builder";

export class EventFactory {
  public _begin: Date;
  public _end: Date;
  public searchText: string = "";
  public productQuery: ProductQuery;

  constructor(
    public venue: Venue, 
    public db: AngularFirestore, 
    public httpClient: HttpClient,
    public authService: AuthService,
    public toast: ToastService,
) {
    if (this.venue != null) this._start();
  }

  private _start() {
    this._begin = this.getInitialBeginDate();
    this._end = this.getInitialEndDate();
    this.productQuery = new ProductQuery();
    this._fetch();
  }

  public refresh() {
    this._start();
  }

  private getInitialBeginDate(): Date {
    return moment()
      .tz(this.venue.cityTimezone)
      .startOf("M")
      .toDate();
  }
  private getInitialEndDate(): Date {
    return moment()
      .tz(this.venue.cityTimezone)
      .endOf("M")
      .add(1, "M")
      .toDate();
  }
  public onNextCalendarMonthPressed() {
    this._begin = moment(this._begin).toDate();
    this._end = moment(this._end)
      .add(1, "M")
      .toDate();

    this._clean();
    this._fetch();
  }
  public onPreviousCalendarMonthPressed() {
    this._begin = moment(this._begin)
      .subtract(1, "M")
      .toDate();
    this._end = moment(this._end).toDate();
    this._clean();
    this._fetch();
  }

  private _products: Product[];
  private _passes: Pass[];
  private _mapPassesToDate: Map<string, Pass[]>;

  private async _fetch() {
    this._passes = [];

    //DONE: GET ALL ACTIVE PRODUCT REFERENCES FOR GIVEN VENUE
    this._products = await this.getAllActiveProducts();

    let promises = [];
    for (var i = 0; i < this._products.length; i++) {
      promises.push(this.getAllEventsForProduct(this._products[i]));
    }

    let result: Pass[][] = [];
    result = await Promise.all(promises);

    let flatten = result.reduce((acc, next) => acc.concat(next), []);

    this._mapPassesToDate = new Map([]);
    flatten.forEach(pass => {
      let passesOnDate = this._mapPassesToDate.get(pass.event.id);
      if (passesOnDate == null) {
        this._mapPassesToDate.set(pass.event.id, [pass]);
      } else {
        passesOnDate.push(pass);
        this._mapPassesToDate.set(
          pass.event.id,
          passesOnDate.sort((a, b) => a.rank - b.rank)
        );
      }
    });
  }

  private async getAllActiveProducts(): Promise<Product[]> {
    const userJWT = await this.authService.user.getIdToken();

    return this.httpClient
        .get(
            `${environment.apiBaseUrl}/api/v1/products`,
            {
                headers: new HttpHeaders().set('Authorization', `${userJWT}`),
                params: {
                    venueId: this.venue.id,
                    showHidden: "false",
                  },
            },
        )
        .toPromise()
        .then((productsResponse) => {
            return Array.from(productsResponse as any[])
                .map(productResponse => new ProductBuilder().loadAPIResponse(productResponse).build());
        })
        .catch((error) => {
            this.toast.error("Could not get products. Please try again.", "Error", {
                opacity: 1,
            });
            throw(error);
        });
  }

  private async getAllEventsForProduct(product: Product): Promise<Pass[]> {
    const userJWT = await this.authService.user.getIdToken();

    return this.httpClient
    .get(
        `${environment.apiBaseUrl}/api/v1/products/${product.id}/events`,
        {
            headers: new HttpHeaders().set('Authorization', `${userJWT}`),
            params: {
                id: product.id,
                begin: this._begin.getTime().toString(),
                end: this._end.getTime().toString(),
              },
        },
    )
    .toPromise()
    .then((eventsForProductResponse) => {
        return Array.from(eventsForProductResponse as any[])
            .map((eventForProduct) => {
                return new Pass (
                    new CalendarEventBuilder().loadAPIResponse(eventForProduct).build(),
                    product,
                )
            });
    })
    .catch((error) => {
        this.toast.error("Could not get events. Please try again.", "Error", {
            opacity: 1,
        });
        throw(error);
    });
  }

  public getEventsForDate(date: string): Pass[] {
    return this._mapPassesToDate.get(date);
  }

  public setPassesForDate(date: string, passes: Pass[]): void {
    this._mapPassesToDate.set(date, passes);
  }

  private _clean() {}

  //TODO: MAP EACH DAY TO A LIST OF OBSERVABLE PASSES
}
